import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, ArrowRight, Check, UserPlus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import type { Branch, CustomerDetail } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { DEFAULT_NATIONALITY } from '../constants'
import { mapCustomerProblem } from '../field-errors'
import { useCreateCustomer } from '../mutations'
import { useDuplicateCheck } from '../use-duplicate-check'
import { customerWizardSchema, toCustomerWriteRequest } from '../validation'
import type { CustomerWizardInput, CustomerWizardValues } from '../validation'
import { WIZARD_STEPS, stepForField, stepIndexOf } from '../wizard-steps'
import { AddressStep } from './address-step'
import { ConsentsStep } from './consents-step'
import { ContactStep } from './contact-step'
import { IdentityStep } from './identity-step'
import { NextOfKinStep } from './next-of-kin-step'
import { ReviewStep } from './review-step'

/**
 * Customer capture.
 *
 * One form across six steps rather than six forms: every value stays in the same form state, so
 * stepping back and forward loses nothing, and a step is a filter over which fields are on screen
 * rather than a submission of its own. That is the whole reason a mis-click costs nothing here.
 *
 * The keyboard path runs the whole way through — Tab between fields, Enter to leave a step, Alt
 * with the arrow keys to move between steps — because this screen is worked dozens of times a day
 * by someone whose other hand is holding a Ghana Card.
 */
/** Controls with their own answer to Enter. The wizard leaves the key alone inside these. */
const KEEPS_ENTER = 'textarea, button, a, summary, [role="button"], [role="combobox"]'

export interface CustomerWizardProps {
  branches: Branch[]
  /** The branch the operator is working in, pre-selected but changeable. */
  defaultBranchId: string
  onCreated: (customer: CustomerDetail) => void
  onCancel: () => void
}

/**
 * The review step renders the parsed form rather than its raw values, which is the same parse the
 * submission itself goes through. Reaching this step means every earlier one passed, so the
 * failure branch is defensive rather than expected — and it says what to do rather than showing a
 * blank panel.
 */
function Review({ values, branches }: { values: CustomerWizardInput; branches: Branch[] }) {
  const parsed = customerWizardSchema.safeParse(values)

  if (!parsed.success) {
    return (
      <Alert tone="warning">
        <AlertTitle>Something on an earlier step is still incomplete</AlertTitle>
        <AlertDescription>
          Step back through the wizard — the step that needs attention will show the field.
        </AlertDescription>
      </Alert>
    )
  }

  return <ReviewStep request={toCustomerWriteRequest(parsed.data)} branches={branches} />
}

export function CustomerWizard({
  branches,
  defaultBranchId,
  onCreated,
  onCancel,
}: CustomerWizardProps) {
  const [stepIndex, setStepIndex] = useState(0)
  const [furthestStep, setFurthestStep] = useState(0)
  const [unattached, setUnattached] = useState<string[]>([])
  const panelRef = useRef<HTMLDivElement | null>(null)

  /*
   * Three generics rather than one: the controls hold the input view of the form — an unmade
   * choice is an empty string — while a completed submission hands the handler the parsed view,
   * with every choice narrowed to a value the API will accept.
   */
  const form = useForm<CustomerWizardInput, unknown, CustomerWizardValues>({
    resolver: zodResolver(customerWizardSchema),
    // Validation on leaving a field, so a correction is offered while the operator is still on
    // that line rather than six fields later.
    mode: 'onBlur',
    defaultValues: {
      branchId: defaultBranchId,
      firstName: '',
      middleName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      placeOfBirth: '',
      nationality: DEFAULT_NATIONALITY,
      maritalStatus: '',
      ghanaCardNumber: '',
      ghanaCardIssueDate: '',
      ghanaCardExpiryDate: '',
      phoneNumber: '',
      altPhoneNumber: '',
      email: '',
      occupation: '',
      employerOrBusinessName: '',
      monthlyIncomeEstimateMinor: undefined,
      residence: {
        ghanaPostGps: '',
        streetOrArea: '',
        landmark: '',
        town: '',
        district: '',
        region: '',
      },
      hasBusinessAddress: false,
      business: {
        ghanaPostGps: '',
        streetOrArea: '',
        landmark: '',
        town: '',
        district: '',
        region: '',
      },
      nextOfKin: [{ fullName: '', relationship: '', phoneNumber: '', address: '' }],
      consentAccountOperation: false,
      consentCreditBureau: false,
      consentMarketing: false,
      consentBiometricCapture: false,
    },
  })

  const ghanaCardDuplicate = useDuplicateCheck('ghanaCardNumber')
  const phoneDuplicate = useDuplicateCheck('phoneNumber')
  const create = useCreateCustomer()

  const step = WIZARD_STEPS[stepIndex]
  const isReview = step.id === 'review'
  const isDirty = form.formState.isDirty
  const isSubmitted = create.isSuccess

  // A reload or a closed tab is the one way progress can still be lost, so the browser is asked to
  // check. Nothing here can style that prompt, and nothing should try to.
  useEffect(() => {
    if (!isDirty || isSubmitted) {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }

    window.addEventListener('beforeunload', warn)
    return () => {
      window.removeEventListener('beforeunload', warn)
    }
  }, [isDirty, isSubmitted])

  // Moving to a step puts the keyboard in it. Without this the operator's next keystroke goes
  // nowhere and they have to reach for the mouse to start typing again.
  useEffect(() => {
    const panel = panelRef.current
    if (!panel) {
      return
    }

    const first = panel.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([tabindex="-1"]), textarea, [role="combobox"]',
    )

    ;(first ?? panel).focus()
  }, [stepIndex])

  const enterStep = (target: number) => {
    setStepIndex(target)
    setFurthestStep((current) => Math.max(current, target))
  }

  const goNext = async () => {
    const valid = await form.trigger(step.fields)
    if (valid) {
      enterStep(stepIndex + 1)
    }
  }

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1)
    }
  }

  const jumpTo = async (target: number) => {
    if (target === stepIndex) {
      return
    }

    // Backwards is always free — the values are all still there. Forwards is only offered as far
    // as the operator has already reached, and still has to leave the current step valid.
    if (target < stepIndex) {
      setStepIndex(target)
      return
    }

    if (target > furthestStep) {
      return
    }

    const valid = await form.trigger(step.fields)
    if (valid) {
      enterStep(target)
    }
  }

  const submit = form.handleSubmit(
    (values) => {
      setUnattached([])

      create.mutate(toCustomerWriteRequest(values), {
        onSuccess: onCreated,
        onError: (error) => {
          const mapped = mapCustomerProblem(error)

          for (const { path, message } of mapped.fields) {
            form.setError(path, { type: 'server', message })
          }

          setUnattached(mapped.unattached)

          const firstField = mapped.fields[0]
          const target = firstField ? stepForField(firstField.path) : undefined
          if (target) {
            setStepIndex(stepIndexOf(target.id))
          }
        },
      })
    },
    (errors) => {
      // A failure found by the whole-form check belongs on the earliest step that carries it, not
      // on the review step where it cannot be corrected.
      const target = WIZARD_STEPS.findIndex((candidate) =>
        candidate.fields.some((field) => field in errors),
      )

      if (target >= 0) {
        setStepIndex(target)
      }
    },
  )

  return (
    <form
      noValidate
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()

        if (isReview) {
          void submit(event)
        } else {
          void goNext()
        }
      }}
      onKeyDown={(event) => {
        const advance = () => {
          if (isReview) {
            void submit()
          } else {
            void goNext()
          }
        }

        if (event.altKey) {
          if (event.key === 'ArrowRight') {
            event.preventDefault()
            advance()
          }

          if (event.key === 'ArrowLeft') {
            event.preventDefault()
            goBack()
          }

          return
        }

        // Enter leaves a capture step from any field, including a date input — where the browser's
        // own picker swallows the key and implicit submission never happens. Controls that answer
        // Enter themselves keep it: a select opens, a link follows, a button presses.
        //
        // It deliberately stops at the review step. Creating the customer is the one irreversible
        // act in this screen, and a held-down Enter must not be able to reach it past a review
        // nobody read.
        if (
          event.key === 'Enter' &&
          !isReview &&
          !(event.target as HTMLElement).closest(KEEPS_ENTER)
        ) {
          event.preventDefault()
          void goNext()
        }
      }}
    >
      <ol className="flex flex-wrap gap-1 rounded-card border border-border-subtle bg-surface p-1.5">
        {WIZARD_STEPS.map((candidate, index) => {
          const current = index === stepIndex
          const reachable = index <= furthestStep
          const done = index < stepIndex

          return (
            <li key={candidate.id}>
              <button
                type="button"
                disabled={!reachable}
                aria-current={current ? 'step' : undefined}
                onClick={() => void jumpTo(index)}
                className={cn(
                  'flex items-center gap-2 rounded-control px-3 py-1.5 text-xs font-medium transition-colors',
                  current && 'bg-brand-fill text-on-brand-fill',
                  !current && reachable && 'text-body hover:bg-surface-hover hover:text-heading',
                  !reachable && 'cursor-not-allowed text-muted',
                )}
              >
                <span
                  className={cn(
                    'numeric flex size-5 shrink-0 items-center justify-center rounded-full text-2xs tabular-nums',
                    current ? 'bg-on-brand-fill/15' : done ? 'bg-success-fill text-on-success-fill' : 'bg-neutral-subtle',
                  )}
                  aria-hidden
                >
                  {done ? <Check className="size-3" /> : index + 1}
                </span>
                {candidate.label}
              </button>
            </li>
          )
        })}
      </ol>

      <div
        ref={panelRef}
        tabIndex={-1}
        className="space-y-4 rounded-card border border-border-subtle bg-canvas p-4 outline-none"
        aria-labelledby="wizard-step-heading"
      >
        <div className="space-y-0.5">
          <h2 id="wizard-step-heading" className="text-base font-semibold text-heading">
            {step.label}
          </h2>
          <p className="text-sm text-muted">{step.description}</p>
        </div>

        {step.id === 'identity' ? (
          <IdentityStep control={form.control} branches={branches} duplicate={ghanaCardDuplicate} />
        ) : null}
        {step.id === 'contact' ? (
          <ContactStep control={form.control} duplicate={phoneDuplicate} />
        ) : null}
        {step.id === 'address' ? <AddressStep control={form.control} /> : null}
        {step.id === 'nextOfKin' ? <NextOfKinStep control={form.control} /> : null}
        {step.id === 'consents' ? <ConsentsStep control={form.control} /> : null}
        {isReview ? <Review values={form.getValues()} branches={branches} /> : null}
      </div>

      {create.isError ? <ProblemAlert error={create.error} /> : null}

      {unattached.length > 0 ? (
        <Alert tone="danger">
          <div className="space-y-1">
            <AlertTitle>The server refused some values</AlertTitle>
            <AlertDescription>
              <ul className="list-inside list-disc space-y-0.5">
                {unattached.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 rounded-card border border-border-subtle bg-surface p-3">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={create.isPending}>
          Cancel
        </Button>

        <span className="mr-auto text-2xs text-muted">
          Enter moves to the next step. Alt with the left and right arrows steps back and forward.
        </span>

        <Button
          type="button"
          variant="secondary"
          onClick={goBack}
          disabled={stepIndex === 0 || create.isPending}
        >
          <ArrowLeft />
          Back
        </Button>

        {isReview ? (
          <Button type="submit" variant="primary" disabled={create.isPending}>
            <UserPlus />
            {create.isPending ? 'Creating…' : 'Create customer'}
          </Button>
        ) : (
          <Button type="submit" variant="primary">
            Next
            <ArrowRight />
          </Button>
        )}
      </div>
    </form>
  )
}
